import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Workflow as WorkflowIcon, Loader2, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { workflowsApi, workflowRunsApi } from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { withWorkspaceContext } from "@/lib/workspace-navigation";

/**
 * Agent Workflows tab — shows two stacked panels:
 *   1. Workflows that include this agent as an agent-type node. Click to
 *      open the canvas editor.
 *   2. Recent runs assigned to this agent. Click to open the per-run
 *      timeline view.
 *
 * Both panels are scoped server-side so we don't ship the tenant's full
 * workflow list to the client.
 */
export function AgentWorkflowsTab({ agentId }: { agentId: string }) {
  const { tenantId, isAdmin } = useTenant();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const agentContextParams = new URLSearchParams(searchParams);
  agentContextParams.set("agentId", agentId);

  const { data: workflows, isLoading: workflowsLoading } = useQuery({
    queryKey: queryKeys.workflowsByAgent(agentId),
    queryFn: () => workflowsApi.listForAgent(agentId),
    enabled: !!agentId,
  });

  const { data: runs, isLoading: runsLoading } = useQuery({
    queryKey: queryKeys.workflowRuns.byAgent(agentId),
    queryFn: () => workflowRunsApi.listForAgent(agentId, 20),
    enabled: !!agentId,
    // Auto-refresh so a run kicked off elsewhere shows up without a
    // manual reload. Short enough for "feels live" but not so short it
    // hammers the server when the tab's open for a while.
    refetchInterval: 5_000,
  });

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-4">
      {/* ── Workflows including this agent ────────────────────────── */}
      <section>
        <header className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">
            Workflows
          </h2>
          {isAdmin && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  withWorkspaceContext(
                    `/tenants/${tenantId}/workspace?mode=workflows`,
                    agentContextParams,
                  ),
                )
              }
              className="text-xs text-text-muted underline-offset-2 hover:text-text-primary hover:underline"
            >
              All workflows →
            </button>
          )}
        </header>
        {workflowsLoading ? (
          <Loader2 size={16} className="animate-spin text-text-muted" />
        ) : !workflows || workflows.length === 0 ? (
          <div className="rounded-md border border-border bg-surface px-3 py-6 text-center">
            <WorkflowIcon size={28} className="mx-auto mb-2 text-text-muted" />
            <p className="text-xs text-text-muted">
              This agent isn't referenced by any workflow yet. Add an Agent
              node on the workflow canvas and select this agent.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Last run</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {workflows.map((wf) => (
                  <tr
                    key={wf.id}
                    className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-hover"
                    onClick={() =>
                      navigate(
                        withWorkspaceContext(
                          `/tenants/${tenantId}/workflows/${wf.id}`,
                          agentContextParams,
                        ),
                      )
                    }
                  >
                    <td className="px-3 py-2 font-medium text-text-primary">
                      {wf.name}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={wf.status} />
                    </td>
                    <td className="px-3 py-2 text-xs text-text-muted">
                      {wf.lastRunAt
                        ? new Date(wf.lastRunAt).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-text-muted">
                      <ArrowRight size={12} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Recent runs for this agent ────────────────────────────── */}
      <section>
        <header className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">
            Recent runs
          </h2>
          <span className="text-[11px] text-text-muted">
            Auto-refreshing
          </span>
        </header>
        {runsLoading ? (
          <Loader2 size={16} className="animate-spin text-text-muted" />
        ) : !runs || runs.length === 0 ? (
          <p className="rounded-md border border-border bg-surface px-3 py-6 text-center text-xs text-text-muted">
            No runs yet. Kick one off from a workflow's canvas page.
          </p>
        ) : (
          <div className="overflow-hidden rounded-md border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                  <th className="px-3 py-2 text-left">Workflow</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Awaiting</th>
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-left">Duration</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => {
                  const duration = formatDuration(r.startedAt, r.completedAt);
                  return (
                    <tr
                      key={r.id}
                      className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-surface-hover"
                      onClick={() =>
                        navigate(
                          withWorkspaceContext(
                            `/tenants/${tenantId}/workflows/${r.workflowId}/runs/${r.id}`,
                            agentContextParams,
                          ),
                        )
                      }
                    >
                      <td className="px-3 py-2 font-medium text-text-primary">
                        {r.workflowName}
                      </td>
                      <td className="px-3 py-2">
                        <RunStatusBadge status={r.status} />
                      </td>
                      <td className="px-3 py-2 text-xs text-text-muted">
                        {r.awaitingType ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-text-muted">
                        {new Date(r.startedAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-xs text-text-muted">
                        {duration}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: "draft" | "active" | "paused" }) {
  const variant =
    status === "active"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : status === "paused"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400";
  return (
    <Badge className={`${variant} border-transparent text-[10px]`}>
      {status}
    </Badge>
  );
}

export function RunStatusBadge({
  status,
}: {
  status: "running" | "awaiting" | "completed" | "failed" | "cancelled";
}) {
  const variant =
    status === "completed"
      ? "bg-green-500/10 text-green-700 dark:text-green-300"
      : status === "running"
        ? "bg-blue-500/10 text-blue-700 dark:text-blue-300"
        : status === "awaiting"
          ? "bg-amber-500/10 text-amber-700 dark:text-amber-300"
          : status === "failed"
            ? "bg-red-500/10 text-red-700 dark:text-red-300"
            : "bg-gray-500/10 text-gray-600 dark:text-gray-400";
  return (
    <Badge className={`${variant} border-transparent text-[10px]`}>
      {status}
    </Badge>
  );
}

function formatDuration(startedAt: string, completedAt: string | null): string {
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const ms = end - new Date(startedAt).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.floor((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}
